import React, { useState, useEffect, useRef, useCallback } from 'react';
import { collection, query, orderBy, getDocs, limit, startAfter, doc, getDoc, onSnapshot } from 'firebase/firestore';
import { Loader } from 'lucide-react';
import { db } from '../../lib/firebase';
import CreatePost from './CreatePost';
import PostItem from './PostItem';
import PinnedPosts from './PinnedPosts';

import { useSearchParams } from 'react-router-dom';
import { auth } from '../../lib/firebase';

interface Reaction {
  count: number;
  userIds: string[];
}

export interface Post {
  id: string;
  content: string;
  authorId: string;
  authorName: string;
  authorPhoto?: string;
  mediaUrls: string[];
  mediaTypes: string[];
  likes: number;
  comments: number;
  shares: number;
  likedBy: string[];
  reactions?: { [key: string]: Reaction };
  createdAt: any;
  isPinned?: boolean;
  inPinnedSection?: boolean;
}

interface FeedProps {
  onProfileClick: (userId: string) => void;
}

function Feed({ onProfileClick }: FeedProps) {
  const [posts, setPosts] = useState<Post[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastPost, setLastPost] = useState<any>(null);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(0);
  const POSTS_PER_PAGE = 10;
  
  const observer = useRef<IntersectionObserver>();
  const postRefs = useRef<{[key: string]: HTMLDivElement | null}>({});
  const [searchParams] = useSearchParams();


  const handlePostDeleted = useCallback((deletedPostId: string) => {
    setPosts(currentPosts => currentPosts.filter(post => post.id !== deletedPostId));
  }, []);

  const handlePostCreated = useCallback(async () => {
    try {
      const user = auth.currentUser;
      if (!user) return;

      const q = query(
        collection(db, 'posts'),
        orderBy('createdAt', 'desc'),
        limit(POSTS_PER_PAGE)
      );

      const snapshot = await getDocs(q);
      const postData = snapshot.docs.map((doc) => {
        const data = doc.data();
        return {
          id: doc.id,
          createdAt: data.createdAt,
          ...data,
          mediaUrls: data.mediaUrls || [],
          mediaTypes: data.mediaTypes || [],
          reactions: data.reactions || {},
          isPinned: data.isPinned || false,
          inPinnedSection: data.inPinnedSection || false
        } as Post;
      });

      setPosts(postData);
      setLastPost(snapshot.docs[snapshot.docs.length - 1]);
      setHasMore(postData.length === POSTS_PER_PAGE);
    } catch (error) {
      console.error('Error refreshing posts:', error);
    }
  }, []);

  // Scroll to post if postId is in URL
  useEffect(() => {
    const postId = searchParams.get('postId');
    if (postId) {
      const element = document.getElementById(`post-${postId}`);
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          element.classList.add('highlight-post');
          setTimeout(() => element.classList.remove('highlight-post'), 2000);
        }, 100); // Small delay to ensure the post is rendered
      }
    }
  }, [searchParams, posts]);

  // Setup real-time listener for initial posts and updates
  useEffect(() => {
    let isMounted = true;
    let unsubscribe: (() => void) | undefined;

    const fetchPosts = async () => {
      try {
        const user = auth.currentUser;
        if (!user) {
          console.error("User is not authenticated!");
          setError("You must be logged in to see posts.");
          setLoading(false);
          return;
        }

        const q = query(
          collection(db, 'posts'),
          orderBy('createdAt', 'desc'),
          limit(POSTS_PER_PAGE)
        );

        // First, get the initial data
        const initialSnapshot = await getDocs(q);
        if (isMounted) {
          const initialPostData = initialSnapshot.docs.map((doc) => {
            const data = doc.data();
            return {
              id: doc.id,
              createdAt: data.createdAt,
              ...data,
              mediaUrls: data.mediaUrls || [],
              mediaTypes: data.mediaTypes || [],
              reactions: data.reactions || {},
              isPinned: data.isPinned || false,
              inPinnedSection: data.inPinnedSection || false
            } as Post;
          });
          setPosts(initialPostData);
          setLastPost(initialSnapshot.docs[initialSnapshot.docs.length - 1]);
          setHasMore(initialPostData.length === POSTS_PER_PAGE);
          setLoading(false);
        }

        // Then set up the real-time listener for updates
        unsubscribe = onSnapshot(q, (snapshot) => {
          if (!isMounted) return;

          snapshot.docChanges().forEach((change) => {
            if (change.type === 'modified') {
              setPosts(prevPosts => {
                return prevPosts.map(post => {
                  if (post.id === change.doc.id) {
                    const data = change.doc.data();
                    return {
                      ...post,
                      ...data,
                      id: change.doc.id,
                      mediaUrls: data.mediaUrls || [],
                      mediaTypes: data.mediaTypes || [],
                      reactions: data.reactions || {},
                      isPinned: data.isPinned || false,
                      inPinnedSection: data.inPinnedSection || false
                    };
                  }
                  return post;
                });
              });
            }
          });
        }, (error) => {
          console.error('Error in post listener:', error);
          if (isMounted) {
            setError('Failed to listen to post updates.');
          }
        });
      } catch (error) {
        if (!isMounted) return;
        console.error('Error loading posts:', error);
        setError('Failed to load posts. Please try again later.');
        setLoading(false);
      }
    };

    fetchPosts();

    return () => {
      isMounted = false;
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [auth.currentUser?.uid]);

  // Load more posts when scrolling
  const loadMorePosts = useCallback(async () => {
    if (loading || !hasMore) return;

    try {
      setLoading(true);
      const nextPage = page + 1;

      let q = query(
        collection(db, 'posts'),
        orderBy('createdAt', 'desc'),
        startAfter(lastPost),
        limit(POSTS_PER_PAGE)
      );

      const snapshot = await getDocs(q);

      if (snapshot.empty) {
        setHasMore(false);
        return;
      }

      const postData = snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          createdAt: data.createdAt,
          ...data,
          mediaUrls: data.mediaUrls || [],
          mediaTypes: data.mediaTypes || [],
          reactions: data.reactions || {},
          isPinned: data.isPinned || false,
          inPinnedSection: data.inPinnedSection || false
        } as Post;
      });

      setPosts(prev => [...prev, ...postData]);
      setLastPost(snapshot.docs[snapshot.docs.length - 1]);
      setHasMore(postData.length === POSTS_PER_PAGE);
      setPage(nextPage);
    } catch (error) {
      console.error('Error loading more posts:', error);
      setError('Failed to load more posts. Please try again later.');
    } finally {
      setLoading(false);
    }
  }, [loading, hasMore, lastPost, page]);

  const lastPostElementRef = useCallback((node: HTMLDivElement) => {
    if (loading) return;
    
    if (observer.current) observer.current.disconnect();
    
    observer.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasMore) {
        loadMorePosts();
      }
    }, {
      root: null,
      rootMargin: '100px',
      threshold: 0.1
    });
    
    if (node) observer.current.observe(node);
  }, [loading, hasMore, loadMorePosts]);

  return (
    <div className="w-full max-w-3xl mx-auto px-4 py-4">
      <CreatePost onPostCreated={handlePostCreated} />
      <div className="w-full flex items-center justify-between mt-8 mb-4">
        <div className="text-gray-900 font-bold text-lg min-w-0 truncate">📌 Pinned Posts</div>
        <button
              onClick={async () => {
                setLoading(true);
                setPosts([]);
                setLastPost(null);
                setPage(0);
                setHasMore(true);
                await handlePostCreated();
                setLoading(false);
              }}
              className="text-orange-500 hover:text-orange-600 transition-colors shrink-0 ml-2"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="min-w-[24px]">
                <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8"/>
                <path d="M21 3v5h-5"/>
                <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16"/>
                <path d="M3 21v-5h5"/>
              </svg>
            </button>
          </div>
          <PinnedPosts onProfileClick={onProfileClick} onPostDeleted={handlePostDeleted} />
          <div className="space-y-3">
            {posts.map((post, index) => (
              <div
                key={post.id}
                id={`post-${post.id}`}
                ref={(el) => {
                  postRefs.current[post.id] = el;
                  if (index === posts.length - 1) {
                    lastPostElementRef(el as HTMLDivElement);
                  }
                }}
              >
                <PostItem
                  post={post}
                  onProfileClick={onProfileClick}
                  onPostDeleted={handlePostDeleted}
                  highlightedCommentId={searchParams.get('commentId')}
                />
              </div>
            ))}
            {loading && (
              <div className="flex justify-center p-4">
                <Loader className="animate-spin text-blue-500" size={24} />
              </div>
            )}
            {error && (
              <div className="text-red-500 text-center p-4">
                {error}
              </div>
            )}
          </div>
    </div>
  );
}

export default Feed;
export { Feed };